import { getAccessToken } from './auth'

const baseUrl = import.meta.env.VITE_API_BASE_URL

type HeadersProvider = () => Record<string, string>

let _headersProvider: HeadersProvider = () => ({})
let _onUnauthorized: (() => void) | null = null

export function setHeadersProvider(provider: HeadersProvider): void {
  _headersProvider = provider
}

export function setOnUnauthorized(handler: () => void): void {
  _onUnauthorized = handler
}

async function buildHeaders(path: string, includeContentType = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}

  if (includeContentType) {
    headers['Content-Type'] = 'application/json'
  }

  const token = await getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const isLoginPath = path.startsWith('/login')
  if (!isLoginPath) {
    Object.assign(headers, _headersProvider())
  }

  return headers
}

function randomDelay(): Promise<void> {
  const ms = Math.floor(Math.random() * 1700) + 300
  return new Promise((r) => setTimeout(r, ms))
}

async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  const response = await fetch(url, init)

  if (response.status === 429) {
    await randomDelay()
    return fetch(url, init)
  }

  return response
}

function handleResponse(response: Response): void {
  if (response.status === 401 && _onUnauthorized) {
    _onUnauthorized()
    throw new Error('Sesión expirada')
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
}

async function request<T>(path: string, options: RequestInit = {}, includeContentType = false, signal?: AbortSignal): Promise<T> {
  const headers = await buildHeaders(path, includeContentType)
  const response = await fetchWithRetry(`${baseUrl}${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
    signal,
  })

  handleResponse(response)

  // Varios endpoints responden 201/204 sin body (gestion de usuarios: cambios de rol,
  // revocaciones): solo se parsea JSON si hay contenido.
  const text = await response.text()
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

export interface DownloadResult {
  blob: Blob
  filename: string | null
}

async function requestRaw(path: string, accept: string): Promise<DownloadResult> {
  const headers = await buildHeaders(path)
  headers['Accept'] = accept
  const response = await fetchWithRetry(`${baseUrl}${path}`, { headers })

  handleResponse(response)

  const disposition = response.headers.get('Content-Disposition')
  let filename: string | null = null
  if (disposition) {
    const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)"?/i)
    if (match) {
      filename = decodeURIComponent(match[1])
    }
  }

  return { blob: await response.blob(), filename }
}

export const api = {
  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return request<T>(path, {}, false, signal)
  },
  /**
   * GET sin headers propios (ni Authorization ni los del contexto): para los endpoints
   * publicos de Keycloak (/organizations...). Sin headers custom el request es "simple" y el
   * navegador ni siquiera hace preflight CORS.
   */
  async getPublic<T>(path: string): Promise<T> {
    const response = await fetchWithRetry(`${baseUrl}${path}`, {})
    handleResponse(response)
    return response.json() as Promise<T>
  },
  download(path: string, accept: string): Promise<DownloadResult> {
    return requestRaw(path, accept)
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body: JSON.stringify(body) }, true)
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, true)
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, true)
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' })
  },
}
